import * as React from 'react';
import moment from 'moment';
import isEqual from 'react-fast-compare';
import DateTime from 'react-datetime';
import { withRouter } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { parse } from 'query-string';
import notify from '../../../shared/feedback/notifications';
import Loader from 'react-loader';
import { connect } from 'react-redux';
import { Translate } from '../../../shared/LanguageService';
import { assign } from '../../../shared/Assign';
import { UserRole } from '../../../shared/types';
import { handleError } from '../../../shared/ErrorCatching';
import Calendar from '../../calendar/Calendar';
import { toMinMaxTime } from '../../calendar/calendarTimeUtils';
import ICalendar from '../../calendar/ICalendar';
import { ICalendarItem, IErrorResult } from '../../calendar/Types';
import * as Features from '../../../shared/Features';
import * as OrderActions from '../../orders/OrdersActions';
import * as Actions from './actions/SE360PhotographerBookingActions';
import PreferredDateModal from '../../orders/PreferredDateModal';
import { Skeleton } from '@mui/material';
import BookingInformation, { BookingInformationLabels } from './components/BookingInformation';
import {
    bypassesBookingRules,
    findPhotographerCalendarIndex,
    firstBookableDateQueryArgs,
    getDefaultBookingDate,
    isBookingRequest,
    useFirstFinishedValue
} from './utils/se360PhotographerBookingHelper';
import { useFirstBookableDateQuery } from './v2/shared/queries/useFirstBookableDateQuery';
import BookingAdvancedMode, { BookingAdvanceModeLabels } from './components/BookingAdvancedMode';
import WeatherWidget from './components/weatherComponent/WeatherWidget';
import PortfolioModal from '../../photographerPortfolio/PortfolioModal';
import { useTelemetryContext } from '../../../telemetry/telemetryContext';
import { Logger } from '../../../telemetry/loggers/Logger';
import { getUserInfo } from '../../../telemetry/utils';

interface IEvent {
    type: number;
    start: string;
    end: string;
    travelDistance: number;
}

interface IEventSource {
    events: IEvent[];
}

interface IPhotographerCalendar {
    eventSources: IEventSource[];
}

interface ISelectedItem {
    travelDistance: number;
    travelTime: number;
    start: string;
    end: string;
    photographerName: string;
}

interface IState {
    calendars: IPhotographerCalendar[];
    selectedItem: ISelectedItem;
    loading: boolean;
    loadingPage: boolean;
    selectedDate: any;
    isAdvanceMode: boolean;
    selectedRegion: number;
    isFilterByRegion: boolean;
    isBookingAllowed: boolean;
    showLetSe360BookConfirmation: boolean;
    includeTraveltimes: boolean;
    isBookableOnlyBySE360: boolean;
    errors: any;
    showPortfolioModal: boolean;
    selectedPhotographer: any;
}

type Forecast = {
    time: string;
    skyIcon: string;
    precipitation: number;
};

type Location = {
    latitude: number;
    longitude: number;
};

export type LocationData = {
    location: Location;
    date: string;
    sunrise: string;
    sunset: string;
    forecasts: Forecast[];
};

interface IProps {
    logger: Logger;
    photographerCalendars: any;
    selectedDate: any;
    orderInfo: any;
    orderId: number;
    errors: IErrorResult[];
    bookableOnlyBySE360: boolean;
    availableRegions: any[];
    defaultRegionId: number;
    isManualBooking: boolean;
    allowedToLetSE360Book: boolean;
    user: any;
    orderCommandForBooking: any;
    addressPointLocation: Location;
    location: any;
    history: any;
    actions: IActions;
    locationData: LocationData[];
    firstBookableDate: Date | null;
}

type DesiredBookingInformation = {
    notes: string;
    preferredTimeOfPhotographing: Date;
};

let calendarItemType = {
    available: 0,
    se360Booking: 1 << 0,
    scandphotosBooking: 1 << 1,
    blocked: 1 << 2,
    travel: 1 << 3
};

export class PhotographerBooking extends React.Component<IProps, IState> {
    private availableForBookingConstraintId: number = -10000;
    private notAvailableForBookingConstraintId: number = -20000;

    constructor(props: IProps) {
        super(props);

        this.state = {
            calendars: [],
            selectedItem: null,
            loading: true,
            loadingPage: true,
            selectedDate: null,
            isAdvanceMode: false,
            selectedRegion: undefined,
            isFilterByRegion: false,
            isBookingAllowed: true,
            showLetSe360BookConfirmation: false,
            includeTraveltimes: true,
            isBookableOnlyBySE360: false,
            errors: null,
            showPortfolioModal: false,
            selectedPhotographer: null
        };

        this.getPhotographers = this.getPhotographers.bind(this);
        this.onEventDrop = this.onEventDrop.bind(this);
        this.createCalendar = this.createCalendar.bind(this);
        this.onDateChange = this.onDateChange.bind(this);
        this.letSE360BookPhotographer = this.letSE360BookPhotographer.bind(this);
        this.bookPhotographer = this.bookPhotographer.bind(this);
        this.onRegionChanged = this.onRegionChanged.bind(this);
        this.onFilterChange = this.onFilterChange.bind(this);
        this.getTotalMinutes = this.getTotalMinutes.bind(this);
        this.createOrderContinued = this.createOrderContinued.bind(this);
    }

    componentDidMount() {
        const params = parse(this.props.location.search);

        let bookingDate = this.getBookingDate(params.date ? moment(params.date) : null);
        this.getPhotographers(bookingDate);
    }

    updateCalendars = (previousPhotographerCalendars: any[], previousSelectedRegion: number) => {
        let { photographerCalendars } = this.props;
        let { selectedRegion } = this.state;

        let hasChangedPhotographerCalendars =
            photographerCalendars &&
            photographerCalendars.length > 0 &&
            !isEqual(previousPhotographerCalendars, photographerCalendars);

        let hasNoConstructedCalendar =
            photographerCalendars &&
            photographerCalendars.length > 0 &&
            this.state.calendars.length === 0;

        let hasChangedRegion = previousSelectedRegion !== selectedRegion;

        if (hasChangedRegion || hasChangedPhotographerCalendars || hasNoConstructedCalendar) {
            this.createCalendar(photographerCalendars);
        } else if (photographerCalendars && photographerCalendars.length === 0) {
            let { loading, loadingPage } = this.state;

            if (loading || loadingPage) {
                this.setState({ loading: false, loadingPage: false });
            }
        }
    };

    componentDidUpdate(prevProps: IProps, prevState: IState) {
        this.updateCalendars(prevProps.photographerCalendars, prevState.selectedRegion);
    }

    getOrderId = (): number => {
        const params = parse(this.props.location.search);
        return Number(params.orderId);
    };

    getPlanOrderId = () => {
        const params = parse(this.props.location.search);
        return Number(params.planOrderId);
    };

    getNextWorkDay() {
        return getDefaultBookingDate(new Date(), this.props.firstBookableDate);
    }

    componentWillUnmount() {
        this.props.actions.removeOrderCommandForBooking();
    }

    getPhotographersForNewBooking = async (date, executionTime) => {
        const orderCommandForBooking = this.props.orderCommandForBooking;

        let latitude = null;
        let longitude = null;

        if (this.props.addressPointLocation) {
            latitude = this.props.addressPointLocation.latitude;
            longitude = this.props.addressPointLocation.longitude;
        } else {
            try {
                let result = await this.props.actions.getAddressCoordinates(
                    orderCommandForBooking.address.addressLine1,
                    orderCommandForBooking.address.addressPostalNumber,
                    orderCommandForBooking.address.addressCity
                );

                latitude = result.latitude;
                longitude = result.longitude;
            } catch (error) {
                handleError(error);
            }
        }

        return this.props.actions.getphotographersForNewBooking(
            date,
            orderCommandForBooking.address.addressLine1,
            orderCommandForBooking.address.addressPostalNumber,
            orderCommandForBooking.address.addressCity,
            executionTime !== null ? executionTime : orderCommandForBooking.executionTime,
            orderCommandForBooking.products,
            orderCommandForBooking.brokerFirmId,
            orderCommandForBooking.numberOfMeasuredPlans,
            latitude,
            longitude
        );
    };

    getPhotographersForRebooking = (
        date,
        executionTime,
        selectedRegion,
        isAdvanceMode,
        includeTravelTimes
    ) => {
        return this.props.actions.getPhotographers(
            date,
            this.getOrderId(),
            selectedRegion === -1 || selectedRegion === undefined ? null : selectedRegion,
            isAdvanceMode,
            executionTime,
            includeTravelTimes
        );
    };

    getSelectedRegion = () => {
        if (
            Features.hasFeature(Features.EditPhotographerBookingAdvanceMode, this.props.user) &&
            this.state.isAdvanceMode &&
            this.state.isFilterByRegion
        ) {
            return this.state.selectedRegion;
        }

        return null;
    };

    warnAboutTravelTimes = (errors: any[]) => {
        if (
            errors &&
            errors.length !== 0 &&
            Features.hasFeature(Features.EditPhotographerBookingAdvanceMode, this.props.user)
        ) {
            let userNames = Array.from(new Set(errors.map((s) => s.userId)))
                .map((userId) => errors.find((s) => s.userId === userId).userName)
                .join(', ');
            notify.warning(
                Translate('orders/notification/traveling-time-warning') + '\n' + userNames
            );
        }
    };

    getBookingDate = (date) => {
        let dateForBooking = date ? date : this.state.selectedDate;
        let nextWorkDay = this.getNextWorkDay();

        return moment(dateForBooking ? dateForBooking : nextWorkDay);
    };

    getPhotographers(bookingDate: Date, executionTime: number | null = null) {
        let overrideToAdvanceMode: boolean = this.props.user.details.role === UserRole.StylingAdmin;

        this.setState(
            (state) =>
                ({
                    loading: true,
                    selectedDate: bookingDate,
                    isAdvanceMode: state.isAdvanceMode || overrideToAdvanceMode
                } as IState),
            async () => {
                if (this.props.orderCommandForBooking) {
                    return this.getPhotographersForNewBooking(bookingDate, executionTime)
                        .then(() => {
                            this.setState({
                                loading: false,
                                loadingPage: false
                            } as IState);
                        })
                        .catch((error: any) => handleError(error));
                } else {
                    let selectedRegion = this.getSelectedRegion();

                    let overrideToExcludeTravelTime: boolean =
                        this.props.user.details.role === UserRole.StylingAdmin;

                    return this.getPhotographersForRebooking(
                        bookingDate,
                        executionTime,
                        selectedRegion,
                        this.state.isAdvanceMode || overrideToAdvanceMode,
                        overrideToExcludeTravelTime ? false : this.state.includeTraveltimes
                    )
                        .then(() => {
                            this.warnAboutTravelTimes(this.props.errors);

                            this.setState({
                                loading: false,
                                loadingPage: false
                            } as IState);
                        })
                        .catch(handleError);
                }
            }
        );
    }

    getDefaultCalendarSettings(weekDuration: number, index: number) {
        let calendarSettings: any = {
            id: 'calendar' + index,
            selectable: true,
            eventRender: (event, $el) => {
                if (!event.isBackgroundEvent) {
                    let start = event.start.format('HH:mm');
                    let end = event.end.format('HH:mm');

                    let timestring = start + ' - ' + end;
                    $el.html(
                        `<div class="fc-time" data-start="${start}" data-full="${timestring}"><span>${timestring}</span></div>` +
                            '<span class="fc-title">' +
                            event.description +
                            '</span><br/>' +
                            '<span class="fc-title">' +
                            event.title +
                            '</span>' +
                            '<div class="fc-resizer fc-end-resizer"></div>'
                    );
                }
            },
            eventConstraint: 'businessHours',
            selectConstraint: 'businessHours',
            selectHelper: true,
            slotLabelFormat: 'HH:mm',
            timeFormat: 'HH:mm',
            minTime: '07:00:00',
            maxTime: '18:00:00',
            defaultView: 'photographerView',
            highlightToday: false,
            eventDurationEditable: Features.hasFeature(
                Features.EditPhotographerBookingExecutionTime,
                this.props.user
            ),
            snapDuration: '00:05:00',
            views: {
                photographerView: {
                    type: 'agenda',
                    duration: { days: weekDuration }
                }
            },
            header: {
                left: '',
                center: '',
                right: ''
            },
            eventClick: (event) => {
                if (event.type === 0) {
                    return this.onEventDrop(event, 0);
                }
            },
            eventDrop: (event, delta) => {
                return this.onEventDrop(event, delta._days);
            },
            eventResize: (event, delta) => {
                let diff = moment.duration(
                    moment.parseZone(event.end).diff(moment.parseZone(event.start))
                );
                let hours = diff.hours();
                let minutes = diff.minutes();
                let time = hours * 60 + minutes;

                let bookingDate = this.getBookingDate(null);
                this.getPhotographers(bookingDate, time);
                return this.onEventDrop(event, delta._days);
            }
        };

        return calendarSettings;
    }

    recalculateTravelTime = (event, calendars): number => {
        let travelTimeCalendarItemType: number = 1 << 3;
        let travelTime = 0;

        if (event.type === calendarItemType.available) {
            let isSelectedPhotographer = (calendar) => {
                return findPhotographerCalendarIndex(event.userId, calendar) > -1;
            };

            let selectedPhotographersCalendar = calendars.find(isSelectedPhotographer);

            if (selectedPhotographersCalendar) {
                let isSelectedPhotographersEvent = (e) => e.userId === event.userId;

                selectedPhotographersCalendar.eventSources[0].events
                    .filter(isSelectedPhotographersEvent)
                    .forEach((e) => {
                        if (e.type === calendarItemType.available) {
                            if (
                                moment
                                    .parseZone(event.end)
                                    .isBetween(moment.parseZone(e.start), moment.parseZone(e.end))
                            ) {
                                // Get travel time for current item
                                let filtered =
                                    selectedPhotographersCalendar.eventSources[0].events.filter(
                                        (t) => {
                                            return (
                                                t.type === travelTimeCalendarItemType &&
                                                moment
                                                    .parseZone(t.end)
                                                    .isSame(moment.parseZone(e.start))
                                            );
                                        }
                                    );

                                if (filtered.length === 1) {
                                    let travel = filtered[0];
                                    let start: any = moment.parseZone(travel.start);
                                    let end: any = moment.parseZone(travel.end);
                                    travelTime = moment.duration(end - start).asMinutes();
                                }
                            }
                        }
                    });
            }
        }

        return travelTime;
    };

    splitCalendars(myArray: any, chunk_size) {
        let results = [];
        const copy = assign([], myArray);

        while (copy.length) {
            results.push(copy.splice(0, chunk_size));
        }

        return results;
    }

    createCalendarDayNamesFromPhotographers = (photographerCalendars: any[]): any[] => {
        let { apiUrl } = window.appConfig;

        return photographerCalendars.map((photographer) => {
            let name = `<div class="photographer-header" data-photographer-id="${photographer.id}">`;

            let profileImageUrl = photographer.profileImageReference
                ? `${apiUrl}/file/${photographer.profileImageReference}`
                : 'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyB2ZXJzaW9uPSIxLjEiIHZpZXdCb3g9IjAgMCAyNTAgMjUwIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgogPGcgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoLTI2Ni40MyAtNDAxLjY1KSI+CiAgPGcgdHJhbnNmb3JtPSJtYXRyaXgoLjY4MzMzIDAgMCAuNjgzMzMgNTgxLjk0IDU1MC43NykiIGZpbGw9IiNEREREREQiPgogICA8cmVjdCB4PSItNDYxLjczIiB5PSItMjE4LjIzIiB3aWR0aD0iMzY1Ljg1IiBoZWlnaHQ9IjM2NS44NSIgcng9IjAiIHJ5PSIwIiBjb2xvcj0iIzAwMDAwMCIgZmlsbD0iI0RERERERCIvPgogIDwvZz4KICA8ZyB0cmFuc2Zvcm09Im1hdHJpeCguMzQ5MiAwIDAgLjM0OTIgMjg0LjU3IDQxOS43OSkiIGZpbGw9IiNGRkYiPgogICA8ZyBmaWxsPSIjRkZGIj4KICAgIDxwYXRoIGQ9Im0zMDYgMzI1Ljk5YzkwLjU2LTAuMDEgMTIzLjE1LTkwLjY4IDEzMS42OC0xNjUuMTcgMTAuNTEtOTEuNzYtMzIuODgtMTYwLjgyLTEzMS42OC0xNjAuODItOTguNzggMC0xNDIuMTkgNjkuMDU1LTEzMS42OCAxNjAuODIgOC41NCA3NC40ODQgNDEuMTE0IDE2NS4xOCAxMzEuNjggMTY1LjE3eiIvPgogICAgPHBhdGggZD0ibTU1MC45OCA1NDEuOTFjLTAuOTktMjguOTA0LTQuMzc3LTU3LjkzOS05LjQyMS04Ni4zOTMtNi4xMTEtMzQuNDY5LTEzLjg4OS04NS4wMDItNDMuOTgzLTEwNy40Ni0xNy40MDQtMTIuOTg4LTM5Ljk0MS0xNy4yNDktNTkuODY1LTI1LjA4MS05LjY5Ny0zLjgxLTE4LjM4NC03LjU5NC0yNi41MzctMTEuOTAxLTI3LjUxOCAzMC4xNzYtNjMuNCA0NS45NjItMTA1LjE5IDQ1Ljk2NC00MS43NzQgMC03Ny42NTItMTUuNzg2LTEwNS4xNy00NS45NjQtOC4xNTMgNC4zMDgtMTYuODQgOC4wOTMtMjYuNTM3IDExLjkwMS0xOS45MjQgNy44MzItNDIuNDYxIDEyLjA5Mi01OS44NjMgMjUuMDgxLTMwLjA5NiAyMi40NjMtMzcuODczIDcyLjk5Ni00My45ODMgMTA3LjQ2LTUuMDQ1IDI4LjQ1NC04LjQzMyA1Ny40ODktOS40MjIgODYuMzkzLTAuNzY2IDIyLjM4NyAxMC4yODggMjUuNTI1IDI5LjAxNyAzMi4yODQgMjMuNDUzIDguNDU4IDQ3LjY2NiAxNC43MzcgNzIuMDQxIDE5Ljg4NCA0Ny4wNzcgOS45NDEgOTUuNjAzIDE3LjU4MiAxNDMuOTIgMTcuOTI0IDQ4LjMxOC0wLjM0MyA5Ni44NDQtNy45ODMgMTQzLjkyLTE3LjkyNCAyNC4zNzUtNS4xNDUgNDguNTktMTEuNDI0IDcyLjA0MS0xOS44ODQgMTguNzM2LTYuNzU3IDI5Ljc4OS05Ljg5NSAyOS4wMjMtMzIuMjg0eiIvPgogICA8L2c+CiAgPC9nPgogPC9nPgo8L3N2Zz4K';

            name += `<div class="photographer-image" style="background-image: url('${profileImageUrl}')"></div>
                         <div class="photographer-info">
                             <div class="photographer-name">${photographer.name}</div>
                             ${
                                 !this.props.orderInfo.isStylingProduct
                                     ? `<div class='portfolio-button'>${Translate(
                                           'calendars/header/button/portfolio'
                                       )}</div>`
                                     : ''
                             }
                         </div></div>`;

            return name;
        });
    };

    getNumberOfPagesInARow = (): number => {
        let isMobile = window.matchMedia('only screen and (max-width: 760px)');

        return isMobile.matches ? 2 : 5;
    };

    getBlockAllDayEvent = (events, photographer, defaultDate, calendarRowIndex) => {
        if (!events.some((event) => event.allDay === true && event.userId === photographer.id)) {
            let allDayEvent = {
                id: null,
                start: moment(defaultDate),
                end: moment(defaultDate),
                type: calendarItemType.available,
                userId: photographer.id,
                userName: photographer.name,
                eventDurationEditable: false,
                overlap: false,
                rendering: 'background',
                className: 'unavailable'
            };

            allDayEvent.start = moment(
                allDayEvent.start.weekday() === calendarRowIndex
                    ? allDayEvent.start.format()
                    : allDayEvent.start.weekday(calendarRowIndex).format()
            );
            allDayEvent.start.set({
                hour: 0,
                minute: 0,
                second: 0,
                millisecond: 0
            });

            allDayEvent.end = moment(allDayEvent.start);
            allDayEvent.end.set({
                hour: 23,
                minute: 59,
                second: 59,
                millisecond: 59
            });

            return allDayEvent;
        }

        return null;
    };

    getMinAndMaxTimeHours = (events) => {
        return events.reduce(
            (acc, event) => {
                if (moment.parseZone(event.start).hour() < acc.minTimeHours) {
                    acc.minTimeHours = moment.parseZone(event.start).hour();
                }

                if (moment.parseZone(event.end).hour() > acc.maxTimeHours) {
                    acc.maxTimeHours = moment.parseZone(event.end).hour() + 1;
                }

                return acc;
            },
            {
                minTimeHours: 7,
                maxTimeHours: 18
            }
        );
    };

    createNotSelectedEvent = (selectedEvent, calendar, photographer) => {
        let copy = assign({}, selectedEvent);

        let index = calendar.userIds.findIndex((x) => x === photographer.id);

        copy.userId = photographer.id;
        copy.userName = photographer.name;
        copy.className = 'not-selected';
        copy.isMainEventForCalendar = true;
        copy.start = moment.parseZone(copy.start);
        copy.end = moment.parseZone(copy.end);
        copy.travelDistance = photographer.travelDistance / 1000;

        const availableSots = photographer.calendar.items.filter(
            (x) =>
                x.isBackgroundEvent &&
                x.type === calendarItemType.available &&
                moment.duration(
                    moment.parseZone(x.end).diff(moment.parseZone(x.start)),
                    'minutes'
                ) >= copy.duration
        );

        if (availableSots.length > 0) {
            let availableSlot = availableSots[0].start;
            copy.start = moment.parseZone(availableSlot);
            copy.end = moment(copy.start);
            copy.end.add(copy.duration, 'minutes');
        }

        copy.originalStart = copy.start.format();
        copy.originalEnd = copy.end.format();
        copy.start =
            copy.start.weekday() === index
                ? copy.start.format()
                : copy.start.weekday(index).format();
        copy.end =
            copy.end.weekday() === index ? copy.end.format() : copy.end.weekday(index).format();

        return copy;
    };

    createCalendar(photographerCalendarsTotal: any[]) {
        let allSettings = [];

        let selectedEvent = null;

        let calendars = [];

        let numberOfPhotographersInARow = this.getNumberOfPagesInARow();
        let calendarRows = this.splitCalendars(
            photographerCalendarsTotal,
            numberOfPhotographersInARow
        );
        calendarRows.forEach((calendarRow: any, calendarRowIndex: number) => {
            let settings = this.getDefaultCalendarSettings(calendarRow.length, calendarRowIndex);

            settings.defaultDate =
                this.state.selectedDate.day() === 0
                    ? this.state.selectedDate.format()
                    : moment(this.state.selectedDate.format()).isoWeekday(0).format();

            let events = [];

            let selectedEventForPhotographer = null;

            // Due to fullcalender does not work with locale, has not included locale files
            moment.locale('en');

            calendarRow.forEach((photographer: any, calendarColumnIndex: number) => {
                if (photographer.blockWholeDay === true) {
                    let blockAllDayEvent = this.getBlockAllDayEvent(
                        events,
                        photographer,
                        settings.defaultDate,
                        calendarRowIndex
                    );

                    if (blockAllDayEvent) {
                        events.push(blockAllDayEvent);
                    }
                }

                photographer.calendar.items.forEach((item: ICalendarItem) => {
                    let event = assign({}, item);

                    event.travelDistance = photographer.travelDistance / 1000;
                    event.start = moment.parseZone(item.start as any);
                    event.end = moment.parseZone(item.end as any);

                    event.originalStart = event.start.format();
                    event.originalEnd = event.start.format();

                    event.start =
                        event.start.weekday() === calendarColumnIndex
                            ? event.start.format()
                            : event.start.weekday(calendarColumnIndex).format();
                    event.end =
                        event.end.weekday() === calendarColumnIndex
                            ? event.end.format()
                            : event.end.weekday(calendarColumnIndex).format();

                    if (item.isBackgroundEvent) {
                        event.rendering = 'background';

                        if (item.type === calendarItemType.travel) {
                            event.id = this.notAvailableForBookingConstraintId;
                            event.className = 'travel';
                        } else {
                            event.id = this.availableForBookingConstraintId;
                            event.className = 'available';
                        }
                    } else {
                        let isBookingEvent =
                            item.type === calendarItemType.available && !item.isBackgroundEvent;

                        if (isBookingEvent) {
                            if (selectedEvent !== null) {
                                // Mark this event null so another selected event is not added to the calendar
                                event = null;
                            } else {
                                // build up the selected booking event
                                event.constraint = this.availableForBookingConstraintId;
                                event.editable = true;
                                event.className = this.getEventClassNameForColor(item.type);
                                event.disableClick = true;

                                let executionTime = this.props.orderInfo.executionTime;
                                if (executionTime) {
                                    event.end = moment
                                        .parseZone(event.start)
                                        .add(executionTime, 'm')
                                        .format();
                                }

                                let momentStart: any = moment.parseZone(event.start);
                                let momentEnd: any = moment.parseZone(event.end);
                                event.duration = moment
                                    .duration(momentEnd - momentStart)
                                    .asMinutes();
                                event.isMainEventForCalendar = true;
                                event.id = event.id ? event.id : 0;
                                selectedEventForPhotographer = event;
                            }
                        } else {
                            event.title = item.title;
                            event.orderNumber = item.orderNumber;
                            event.className = this.getEventClassNameForColor(item.type);
                            event.description = item.description;
                            event.overlap = false;
                        }
                    }

                    // Don't push another selected event
                    if (event !== null) {
                        events.push(event);
                    }
                });

                if (selectedEvent === null) {
                    selectedEvent = selectedEventForPhotographer;
                }
            });

            // Due to fullcalender does not work with locale, has not included locale files
            moment.locale(this.props.user.details.languageKey);

            let filteredEvents = events.filter((e) => {
                return e.start !== null && e.end !== null;
            });

            settings.eventSources = [{ events: filteredEvents }];

            settings.dayNames = this.createCalendarDayNamesFromPhotographers(calendarRow);

            let { minTimeHours, maxTimeHours } = this.getMinAndMaxTimeHours(filteredEvents);

            const minMaxTime = toMinMaxTime(minTimeHours, maxTimeHours);
            settings.minTime = minMaxTime.minTime;
            settings.maxTime = minMaxTime.maxTime;

            calendars.push({
                items: filteredEvents,
                workingHours: [],
                userIds: calendarRow.map((x) => x.id)
            });

            this.disableBookIfNotAllowed(selectedEvent, filteredEvents);

            allSettings.push(settings);
        });

        if (selectedEvent) {
            selectedEvent.travelTime = this.recalculateTravelTime(selectedEvent, allSettings);

            // setting event for every photographer group
            moment.locale('en');

            let isCalendarRowWithPhotographerWithoutSelectedBookingEvent = (calendar) =>
                calendar.userIds.every((x) => x !== selectedEvent.userId);

            let firstAvailablePhotographerInCalendarRow = (calendar) => (photographerCalendar) =>
                calendar.userIds.some((uId) => uId === photographerCalendar.id) &&
                (this.state.isAdvanceMode || photographerCalendar.isAvailable);

            calendars
                .filter(isCalendarRowWithPhotographerWithoutSelectedBookingEvent)
                .forEach((calendar: ICalendar) => {
                    let photographer = this.props.photographerCalendars.find(
                        firstAvailablePhotographerInCalendarRow(calendar)
                    );

                    if (photographer) {
                        let notSelectedEvent = this.createNotSelectedEvent(
                            selectedEvent,
                            calendar,
                            photographer
                        );

                        calendar.items.push(notSelectedEvent);
                    }
                });

            moment.locale(this.props.user.details.languageKey);
        }

        this.setState({ selectedItem: selectedEvent, calendars: allSettings });
    }

    getEventClassNameForColor(eventType: any) {
        switch (eventType) {
            case calendarItemType.scandphotosBooking:
                return 'scandphotos';
            case calendarItemType.se360Booking:
                return 'se360';
            case calendarItemType.blocked:
                return 'blocked';
            default:
                return 'selected';
        }
    }

    isBookableOnlyBySE360 = (): boolean => {
        let { bookableOnlyBySE360 } = this.props;

        return (
            bookableOnlyBySE360 &&
            Features.hasNotFeature(Features.BookOrdersWithoutTraveltime, this.props.user)
        );
    };

    disableBookIfNotAllowed(selectedItem: any, calendarItems: any[]) {
        let isAllowed = this.isAllowed(selectedItem, calendarItems);
        isAllowed = this.isBookableOnlyBySE360() === true ? false : isAllowed;
        this.setState({ isBookingAllowed: isAllowed } as IState);
    }

    isAllowed(selectedItem: any, calendarItems: any[]) {
        if (selectedItem) {
            calendarItems.forEach((x) => {
                if (x.id !== selectedItem.id) {
                    if (x.type !== calendarItemType.available) {
                        if (
                            (selectedItem.start >= x.start && selectedItem.start <= x.end) ||
                            (selectedItem.end >= x.start && selectedItem.end <= x.end)
                        ) {
                            return false;
                        }
                    }
                    if (x.type === calendarItemType.available) {
                        if (selectedItem.start <= x.start || selectedItem.end >= x.end) {
                            return false;
                        }
                    }
                }
            });
        }

        return calendarItems.length > 1;
    }

    onEventDrop(currentEvent: any, deltaDays: any) {
        let calendars = assign([], this.state.calendars);

        let selectedItem: any = {};

        calendars.forEach((calendar) => {
            let travelTime = 0;
            let currentIndex = findPhotographerCalendarIndex(currentEvent.userId, calendar);
            let foundPhotographer = currentIndex >= 0;
            let travelDistance = 0;

            if (foundPhotographer) {
                calendar.eventSources[0].events.forEach((event) => {
                    let calendarDay = calendar.dayNames[currentIndex + deltaDays];

                    let hasEventsPhotographerInSelectedCalendar =
                        calendarDay.indexOf(event.userName) > -1;

                    let hasNotInitializedSelectedItem =
                        calendarDay.indexOf(selectedItem.userName) < 0;

                    if (
                        calendarDay &&
                        hasEventsPhotographerInSelectedCalendar &&
                        hasNotInitializedSelectedItem
                    ) {
                        // Initialize selected item from event
                        selectedItem.userName = event.userName;
                        selectedItem.userId = event.userId;

                        // Update currentEvent (event we just dropped)
                        currentEvent.userName = selectedItem.userName;
                        currentEvent.userId = selectedItem.userId;
                    }
                });

                calendar.eventSources[0].events.forEach((event) => {
                    if (event.id === currentEvent.id) {
                        event.start = currentEvent.start;
                        event.end = currentEvent.end;
                        event.userName = currentEvent.userName;
                        event.userId = currentEvent.userId;

                        if (event.className === 'not-selected') {
                            event.className = 'selected';
                        } else if (
                            Array.isArray(event.className) &&
                            event.className.some((x) => x === 'not-selected')
                        ) {
                            event.className = event.className.filter((x) => x !== 'not-selected');
                            event.className.push('selected');
                        }
                    }
                });

                for (let i = 0; i < calendar.eventSources[0].events.length; i++) {
                    let event = calendar.eventSources[0].events[i];

                    let isNotTheAttemptedBookingEvent = event.orderId !== currentEvent.orderId;

                    let isAnAvailableEventSlotInTheCalendar =
                        isNotTheAttemptedBookingEvent && event.type === calendarItemType.available;

                    if (isAnAvailableEventSlotInTheCalendar) {
                        let eventStart = moment.parseZone(event.start);

                        let eventEnd = moment.parseZone(event.end);

                        if (
                            moment(currentEvent.end.format()).isBetween(
                                eventStart.format(),
                                eventEnd.format(),
                                // Parameters below are for inclusivity (https://stackoverflow.com/a/57754332/1495325)
                                null,
                                []
                            )
                        ) {
                            let filtered = calendar.eventSources[0].events.filter((t) => {
                                return (
                                    t.type === calendarItemType.travel &&
                                    moment.parseZone(t.end).isSame(moment.parseZone(event.start))
                                );
                            });

                            if (filtered.length === 1) {
                                let travel = filtered[0];
                                let start: any = moment.parseZone(travel.start);
                                let end: any = moment.parseZone(travel.end);
                                travelTime = moment.duration(end - start).asMinutes();
                                travelDistance = travel.travelDistance;
                                break;
                            }
                        }
                    }
                }

                selectedItem.id = currentEvent.id;
                selectedItem.start = currentEvent.start;
                selectedItem.end = currentEvent.end;
                selectedItem.travelTime = travelTime > 0 ? travelTime : '0';
                selectedItem.travelDistance = travelDistance;

                this.disableBookIfNotAllowed(selectedItem, calendar.eventSources[0].events);
            } else {
                calendar.eventSources[0].events.forEach((event) => {
                    if (event.className === 'selected') {
                        event.className = 'not-selected';
                    } else if (
                        Array.isArray(event.className) &&
                        event.className.some((x) => x === 'selected')
                    ) {
                        event.className = event.className.filter((x) => x !== 'selected');
                        event.className.push('not-selected');
                    }
                });
            }
        });

        this.setState({
            calendars: calendars,
            selectedItem: selectedItem
        } as IState);

        return currentEvent;
    }

    onDateChange(value: any) {
        if (!value.isSame(this.state.selectedDate)) {
            let bookingDate = this.getBookingDate(value);
            this.getPhotographers(bookingDate);
        }
    }

    createNewOrder = (calendarItem, desiredBookingInformation?: DesiredBookingInformation) => {
        let orderCommandForBooking = assign({}, this.props.orderCommandForBooking);

        orderCommandForBooking.calendarItem = calendarItem;

        if (desiredBookingInformation) {
            orderCommandForBooking.other.notes = desiredBookingInformation.notes;
            orderCommandForBooking.other.preferredTimeOfPhotographing =
                desiredBookingInformation.preferredTimeOfPhotographing;

            let { logger, user } = this.props;
            let additionalInfo = {
                origin: 'SE360PhotographerBooking.createNewOrder:1032',
                user: getUserInfo(user)
            };
            try {
                logger.logInfo('[Should be booking request]', {
                    ...additionalInfo,
                    existingNotes: orderCommandForBooking.other.notes,
                    existingDate: orderCommandForBooking.other.preferredTimeOfPhotographing,
                    desiredNotes: desiredBookingInformation.notes,
                    desiredDate: desiredBookingInformation.preferredTimeOfPhotographing,
                    calendarItem
                });
            } catch (err) {
                logger.logException(err, additionalInfo);
            }
        }

        if (calendarItem === null) {
            orderCommandForBooking.bookDirectly = false;
        }

        orderCommandForBooking.isBookingRequest = isBookingRequest(
            calendarItem,
            desiredBookingInformation
        );

        this.setState({ loading: true } as IState, () => {
            this.props.actions
                .createOrder(orderCommandForBooking, orderCommandForBooking.orderCollectionId)
                .then((createOrderResult: any) => {
                    this.props.actions.removeOrderCommandForBooking();
                    notify.success(Translate('orders/notification/order-created'));
                    this.setState({
                        loading: false,
                        loadingPage: false
                    } as IState);

                    this.createOrderContinued(createOrderResult);
                })
                .catch((error: any) =>
                    handleError(error, (errors: any): void => {
                        this.setState({
                            errors: errors,
                            loading: false,
                            loadingPage: false
                        } as IState);
                    })
                );
        });
    };

    bookPhotographer(item, desiredBookingInformation?: DesiredBookingInformation) {
        let calendarItem: any = null;
        if (item) {
            let bookingDate = this.state.selectedDate;
            let startDate = new Date(
                bookingDate.year(),
                bookingDate.month(),
                bookingDate.date(),
                moment(item.start).hours(),
                moment(item.start).minutes(),
                0
            );

            let endDate = new Date(
                bookingDate.year(),
                bookingDate.month(),
                bookingDate.date(),
                moment(item.end).hours(),
                moment(item.end).minutes(),
                0
            );

            calendarItem = {
                id: item.id,
                orderId: null,
                end: endDate,
                start: startDate,
                userId: item.userId,
                type: calendarItemType.se360Booking
            };
        }

        if (this.props.orderCommandForBooking) {
            if (this.state.showLetSe360BookConfirmation) {
                // This is when we come from "let s360 book photographer"
                this.setState({ showLetSe360BookConfirmation: false } as IState, () => {
                    this.createNewOrder(calendarItem, desiredBookingInformation);
                });
            } else {
                // This is when customer books photographer directly
                this.createNewOrder(calendarItem, desiredBookingInformation);
            }
        } else {
            if (calendarItem !== null) {
                calendarItem.orderId = this.getOrderId();
            }

            this.setState({ loading: true } as IState);
            this.props.actions
                .updatePhotographer({
                    calendarItem: calendarItem,
                    planOrderId: this.getPlanOrderId(),
                    isAdvanced: this.state.isAdvanceMode
                })
                .then(() => {
                    if (calendarItem) {
                        notify.success(Translate('se360/photographerbooking/page/toastr/success'));
                    }
                    this.setState({
                        loading: false,
                        loadingPage: false
                    } as IState);
                    this.redirect();
                })
                .catch((error: any) =>
                    handleError(error, (errors: any): void => {
                        this.setState({
                            errors: errors,
                            loading: false,
                            loadingPage: false
                        } as IState);
                    })
                );
        }
    }

    createOrderContinued(result: any) {
        let routeParams: any = {
            pathname: '/orders',
            search: ''
        };

        let { digitalStylingOrderId, planOrderId } = result;

        if (digitalStylingOrderId && planOrderId && !result.isMeasured) {
            routeParams.pathname = `/orders/${digitalStylingOrderId}/digital-styling`;
            routeParams.search = `?newOrder=1&redirectTo=/orders/${planOrderId}/plan?newOrder=1`;
        } else if (digitalStylingOrderId) {
            routeParams.pathname = `/orders/${digitalStylingOrderId}/digital-styling`;
            routeParams.search = '?newOrder=1';
        } else if (result.planOrderId && !result.isMeasured) {
            routeParams.pathname = `/orders/${result.planOrderId}/plan`;
            routeParams.search = '?newOrder=1';
        } else if (result.stylingOrderId) {
            routeParams.pathname = `/orders/${result.stylingOrderId}`;
        } else {
            routeParams.pathname = `/orders/${result.photoOrderId}`;
        }

        this.props.history.push(routeParams);
    }

    letSE360BookPhotographer() {
        this.setState({ showLetSe360BookConfirmation: true } as IState);
    }

    redirect() {
        let params = parse(this.props.location.search);
        this.props.history.push(params.redirectTo);
    }

    onClickAdvancedMode = () => {
        this.setState({ isAdvanceMode: true }, () => {
            let { selectedDate } = this.state;
            let bookingDate = this.getBookingDate(selectedDate);
            this.getPhotographers(bookingDate);
        });
    };

    onClickNormalMode = () => {
        this.setState(
            { isAdvanceMode: false, isFilterByRegion: false, includeTraveltimes: true },
            () => {
                let { selectedDate } = this.state;
                let bookingDate = this.getBookingDate(selectedDate);
                this.getPhotographers(bookingDate);
            }
        );
    };

    onClickWorkArea = () => {
        this.setState({ isFilterByRegion: false }, () => {
            let { selectedDate } = this.state;
            let bookingDate = this.getBookingDate(selectedDate);
            this.getPhotographers(bookingDate);
        });
    };

    onRegionChanged(regionId: number) {
        this.setState({ selectedRegion: regionId } as IState, () => {
            let { selectedDate } = this.state;
            let bookingDate = this.getBookingDate(selectedDate);
            this.getPhotographers(bookingDate);
        });
    }

    onClickRegion = (regionId: number) => {
        this.setState(
            { selectedRegion: regionId, isAdvanceMode: true, isFilterByRegion: true } as IState,
            () => {
                let { selectedDate } = this.state;
                let bookingDate = this.getBookingDate(selectedDate);
                this.getPhotographers(bookingDate);
            }
        );
    };

    onFilterChange() {
        this.setState(
            (state) => ({ isFilterByRegion: !state.isFilterByRegion } as IState),
            () => {
                let bookingDate = this.getBookingDate(this.state.selectedDate);
                this.getPhotographers(bookingDate);
            }
        );
    }

    getTotalMinutes() {
        if (!this.state.selectedItem.start || !this.state.selectedItem.end) {
            return 0;
        }

        let diff = moment.duration(
            moment
                .parseZone(this.state.selectedItem.end)
                .diff(moment.parseZone(this.state.selectedItem.start))
        );
        let hours = diff.hours();
        let minutes = diff.minutes();

        return hours * 60 + minutes;
    }

    onChangeIncludeTravelTimes = (checked: boolean) => {
        this.setState({ includeTraveltimes: checked }, () => {
            let bookingDate = this.getBookingDate(null);
            this.getPhotographers(bookingDate);
        });
    };

    displayTime(input) {
        if (typeof input === 'string') {
            return moment.parseZone(input).format('LT');
        }
        return moment(input).format('LT');
    }

    saveDataFromPreferredDateModal(result: any) {
        let desiredBookingInformation = result.reduce((acc, elem) => {
            if (elem.name === 'notes') {
                acc.notes = elem.value;
            } else if (elem.name === 'preferredTimeOfPhotographing') {
                acc.preferredTimeOfPhotographing = elem.value;
            }
            return acc;
        }, {});

        this.bookPhotographer(false, desiredBookingInformation);
    }

    openPhotographerPortfolioModal = (photographerName: string, photographerId: any) => {
        this.setState({
            showPortfolioModal: true,
            selectedPhotographer: { name: photographerName, id: photographerId }
        });
    };

    render() {
        let hasEditingFeature = Features.hasFeature(
            Features.EditPhotographerBookingExecutionTime,
            this.props.user
        );
        let calendars = this.props.photographerCalendars;

        let bookingInformationLabels: BookingInformationLabels = {
            bookButton: Translate('se360/photographerbooking/page/button/book-photographer'),
            travelDistance: Translate('se360/photographerbooking/page/text/travel-distance'),
            travelTime: Translate('se360/photographerbooking/page/text/travel-time'),
            whomToBook: Translate(
                this.props.orderInfo && this.props.orderInfo.isStylingProduct
                    ? 'se360/photographerbooking/page/text/stylinguser'
                    : 'se360/photographerbooking/page/text/photographer'
            )
        };

        let bookingAdvanceModeLabels: BookingAdvanceModeLabels = {
            radioButtonNormalMode: Translate(
                'se360/photographerbooking/page/advanced/toggle/normal'
            ),
            radioButtonAdvanceMode: Translate(
                'se360/photographerbooking/page/advanced/toggle/advanced'
            ),
            radioButtonWorkArea: Translate(
                'se360/photographerbooking/page/advanced/toggle/workarea'
            ),
            radioButtonRegion: Translate('se360/photographerbooking/page/advanced/toggle/region'),
            regionLabel: Translate('se360/photographerbooking/page/advanced/region/label'),
            regionPlaceholder: Translate(
                'se360/photographerbooking/page/advanced/region/placeholder'
            ),
            checkboxLabelIncludeTravelTimes: Translate(
                'se360/photographerbooking/page/advanced/include-traveltimes'
            )
        };

        return (
            <Loader loaded={!this.state.loadingPage}>
                <div className="row">
                    <div className="inline-block col">
                        <div className="vertical-top col">
                            <DateTime
                                locale={this.props.user.details.languageKey}
                                input={false}
                                timeFormat={false}
                                onChange={this.onDateChange}
                                value={this.state.selectedDate}
                                isValidDate={(date) => {
                                    return date.isAfter(moment().subtract(1, 'day'));
                                }}
                                utc={true}
                                className="photo-booking-rdt"
                            />
                        </div>
                        {this.props.locationData && this.props.orderInfo ? (
                            <div className="inline-block col">
                                <WeatherWidget
                                    city={this.props.orderInfo.city}
                                    weatherForecasts={this.props.locationData.forecasts}
                                    sunrise={this.props.locationData.sunrise}
                                    sunset={this.props.locationData.sunset}
                                />
                            </div>
                        ) : (
                            <div className="inline-block col">
                                <Skeleton variant="rectangular" width="320px" height={'250px'} />
                            </div>
                        )}
                        <div className="vertical-top col photographer-booking-main">
                            {this.state.selectedItem !== null &&
                            calendars &&
                            calendars.length !== 0 ? (
                                <BookingInformation
                                    labels={bookingInformationLabels}
                                    selectedDate={this.state.selectedDate.format('L')}
                                    sessionTime={`${this.displayTime(
                                        this.state.selectedItem.start
                                    )} - ${this.displayTime(
                                        this.state.selectedItem.end
                                    )} (${this.getTotalMinutes()} ${Translate(
                                        'se360/photographerbooking/page/text/minutes'
                                    )})`}
                                    originalSessionTime={
                                        this.props.orderInfo.executionTime !==
                                        this.getTotalMinutes()
                                            ? Translate(
                                                  'se360/photographerbooking/page/text/original-executiontime',
                                                  {
                                                      minutes: this.props.orderInfo.executionTime
                                                  }
                                              )
                                            : null
                                    }
                                    selectedPersonToBook={this.state.selectedItem.userName}
                                    travelTime={
                                        this.state.selectedItem.travelTime
                                            ? `${this.state.selectedItem.travelTime} ${Translate(
                                                  'se360/photographerbooking/page/text/minutes'
                                              )}`
                                            : null
                                    }
                                    travelDistance={
                                        this.state.selectedItem.travelDistance
                                            ? `${
                                                  this.state.selectedItem.travelDistance > 1
                                                      ? Math.round(
                                                            this.state.selectedItem.travelDistance
                                                        )
                                                      : this.state.selectedItem.travelDistance.toFixed(
                                                            1
                                                        )
                                              } ${Translate(
                                                  'se360/photographerbooking/page/text/kilometers'
                                              )}`
                                            : null
                                    }
                                    disableBooking={this.state.isBookingAllowed === false}
                                    onBook={() => {
                                        this.bookPhotographer(this.state.selectedItem);
                                    }}
                                />
                            ) : null}

                            {this.props.orderInfo &&
                                !this.props.orderInfo.isStylingProduct &&
                                this.props.orderCommandForBooking &&
                                (this.props.allowedToLetSE360Book ||
                                    (calendars && calendars.length === 0) ||
                                    this.isBookableOnlyBySE360()) && (
                                    <Button
                                        block
                                        bsStyle="primary"
                                        data-testid="booking-let-se360-book"
                                        onClick={() => {
                                            if (!hasEditingFeature) {
                                                this.letSE360BookPhotographer();
                                            } else {
                                                this.bookPhotographer(null);
                                            }
                                        }}
                                    >
                                        {hasEditingFeature
                                            ? Translate(
                                                  'se360/photographerbooking/page/button/cancel'
                                              )
                                            : Translate(
                                                  'se360/photographerbooking/page/button/let-se360-book'
                                              ) + '...'}
                                    </Button>
                                )}
                        </div>
                        {Features.hasFeature(
                            Features.EditPhotographerBookingAdvanceMode,
                            this.props.user
                        ) ? (
                            <BookingAdvancedMode
                                includeTravelTimes={this.state.includeTraveltimes}
                                availableRegions={this.props.availableRegions}
                                onClickAdvancedMode={this.onClickAdvancedMode}
                                onClickNormalMode={this.onClickNormalMode}
                                onClickWorkArea={this.onClickWorkArea}
                                onChangeIncludeTravelTimes={this.onChangeIncludeTravelTimes}
                                onRegionChange={this.onRegionChanged}
                                onClickRegion={this.onClickRegion}
                                selectedRegionId={
                                    this.state.selectedRegion !== undefined
                                        ? this.state.selectedRegion
                                        : this.props.defaultRegionId
                                }
                                labels={bookingAdvanceModeLabels}
                            />
                        ) : null}
                    </div>

                    {calendars && calendars.length === 0 && !hasEditingFeature && (
                        <div className="inline-block vertical-top cal-col">
                            <h1 className="no-photographer-header">
                                {Translate(
                                    'se360/photographerbooking/page/header/no-photographers'
                                )}
                            </h1>
                        </div>
                    )}

                    <div
                        className={
                            'inline-block col vertical-top cal-col' +
                            (this.state.isAdvanceMode ? ' advanced-booking' : '')
                        }
                    >
                        {this.state.calendars &&
                            (this.isBookableOnlyBySE360()
                                ? Features.hasFeature(
                                      Features.BookOrdersWithoutTraveltime,
                                      this.props.user
                                  )
                                : true) &&
                            this.state.calendars.map((calendar: any, index: number) => {
                                return (
                                    <Calendar
                                        settings={calendar}
                                        calendarItems={null}
                                        workingHours={null}
                                        onChange={() => {}}
                                        key={'calendar_' + index}
                                        user={this.props.user}
                                        selectedDate={this.state.selectedDate}
                                        onPortfolioClick={this.openPhotographerPortfolioModal}
                                    />
                                );
                            })}
                        {this.state.calendars &&
                            (this.isBookableOnlyBySE360()
                                ? Features.hasNotFeature(Features.ViewLogs, this.props.user)
                                : '') && (
                                <div>
                                    <h1>
                                        <i className="material-icons material-icons-info no-label md-60">
                                            warning
                                        </i>{' '}
                                        {Translate(
                                            'se360/photographerbooking/page/header/bad-traveltimes-let-se360-book'
                                        )}
                                    </h1>
                                </div>
                            )}
                    </div>
                </div>

                <Loader loaded={!this.state.loading} scale={2.0} />
                <div className={'order-loader' + (this.state.loading ? ' loader-on' : '')} />

                {this.state.showLetSe360BookConfirmation && (
                    <PreferredDateModal
                        onBookDirectly={() => {
                            this.setState({
                                showLetSe360BookConfirmation: false
                            } as IState);
                        }}
                        onClose={() => {
                            this.setState({
                                showLetSe360BookConfirmation: false
                            } as IState);
                        }}
                        onApprove={(bool: boolean, result: string) => {
                            this.saveDataFromPreferredDateModal(result);
                        }}
                        notes={
                            this.props.orderCommandForBooking
                                ? this.props.orderCommandForBooking.other.notes
                                : ''
                        }
                        showNotes={true}
                    />
                )}

                {this.state.showPortfolioModal && (
                    <PortfolioModal
                        show={this.state.showPortfolioModal}
                        photographer={this.state.selectedPhotographer}
                        onClose={() => {
                            this.setState({ showPortfolioModal: false });
                            this.setState({ selectedPhotographer: null });
                        }}
                    />
                )}
            </Loader>
        );
    }
}

function mapStateToProps(state: any) {
    return {
        photographerCalendars: state.SE360PhotographerBooking.photographerCalendars,
        orderInfo: state.SE360PhotographerBooking.orderInfo,
        errors: state.SE360PhotographerBooking.errors,
        bookableOnlyBySE360: state.SE360PhotographerBooking.bookableOnlyBySE360,
        availableRegions: state.SE360PhotographerBooking.availableRegions,
        defaultRegionId: state.SE360PhotographerBooking.defaultRegionId,
        isManualBooking: state.SE360PhotographerBooking.isManualBooking,
        allowedToLetSE360Book: state.SE360PhotographerBooking.allowedToLetSE360Book,
        user: state.user,
        orderCommandForBooking: state.orders.orderCommandForBooking,
        addressPointLocation: state.SE360PhotographerBooking.addressPointLocation,
        locationData: state.SE360PhotographerBooking.locationData
    };
}

function mapDispatchToProps(dispatch: any) {
    return {
        actions: {
            getPhotographers: (
                date: Date,
                orderId: number,
                regionId?: number,
                isAdvanceMode?: boolean,
                executionTime?: number,
                includeTraveltimes?: boolean
            ) =>
                dispatch(
                    Actions.getPhotographerCalendars(
                        date,
                        orderId,
                        regionId,
                        isAdvanceMode,
                        executionTime,
                        includeTraveltimes
                    )
                ),
            getphotographersForNewBooking: (
                date: Date,
                address: string,
                postalCode: string,
                city: string,
                executionTime: number,
                productIds: number[],
                brokerFirmId: number,
                numberOfMeasuredPlans?: number,
                lat?: number,
                lon?: number
            ) =>
                dispatch(
                    Actions.getphotographersForNewBooking(
                        date,
                        address,
                        postalCode,
                        city,
                        executionTime,
                        productIds,
                        brokerFirmId,
                        numberOfMeasuredPlans,
                        lat,
                        lon
                    )
                ),
            updatePhotographer: (calendarItem: any) =>
                dispatch(Actions.updatePhotographer(calendarItem)),
            createOrder: (order: any, orderCollectionId?: number) =>
                dispatch(OrderActions.createOrder(order, orderCollectionId)),
            removeOrderCommandForBooking: () =>
                dispatch(OrderActions.removeOrderCommandForBooking()),
            getAddressCoordinates: (address: string, postalCode: string, city: string) =>
                dispatch(Actions.getAddressCoordinates(address, postalCode, city))
        }
    };
}

interface IActions {
    getPhotographers: (
        date: Date,
        orderId: number,
        regionId?: number,
        isAdvanceMode?: boolean,
        executionTime?: number,
        includeTraveltimes?: boolean
    ) => Promise<any>;
    getphotographersForNewBooking: (
        date: Date,
        address: string,
        postalCode: string,
        city: string,
        executionTime: number,
        productIds: number[],
        brokerFirmId: number,
        numberOfMeasuredPlans?: number,
        lat?: number,
        lon?: number
    ) => Promise<any>;
    updatePhotographer: (calendarItem: any) => any;
    createOrder: (order: any, orderCollectionId?: number) => Promise<any>;
    removeOrderCommandForBooking: () => Promise<any>;
    getAddressCoordinates: (address: string, postalCode: string, city: string) => any;
}

export function PhotographerBookingWithLogger(props) {
    let { logger } = useTelemetryContext();
    let role = props.user && props.user.details ? props.user.details.role : null;
    let { firstBookableDate, isFinished } = useFirstBookableDateQuery({
        ...firstBookableDateQueryArgs(props.orderCommandForBooking, props.location.search),
        enabled: !bypassesBookingRules(role)
    });
    // The page picks its start day only once, on mount.
    let startDate = useFirstFinishedValue(firstBookableDate, isFinished);

    if (!startDate.isReady) {
        return <Skeleton variant="rectangular" height={600} aria-busy="true" />;
    }

    return (
        <PhotographerBooking {...props} logger={logger} firstBookableDate={startDate.value} />
    );
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withRouter(PhotographerBookingWithLogger));
